import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { useYowanPreviewSearch } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuth } from '../app/YowanAuthContext';
import {
  buildContentMediaAssetOptions,
  fetchYowanAdminBlogList,
  fetchYowanAdminBlogAuthorAccessList,
  fetchYowanAdminContentMediaAssets,
  fetchYowanBlogAuthorMediaAssets,
  fetchYowanAdminNewsList,
  formatYowanAdminDateTime,
  getYowanImageDisplayName,
  getYowanImagePreviewSrc,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
  YOWAN_CONTENT_ADMIN_PAGE_SIZE,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const ADMIN_ROUTE_KEY = '/admin';
const ADMIN_CONTENT_PAGE_SIZE = YOWAN_CONTENT_ADMIN_PAGE_SIZE;
const STAGING_DOMAIN_FALLBACK_HREF = '/admin/deploy/staging-domain-fallback';
const STAGING_SWITCH_DEVELOPER_EMAIL = 'shimazaki@shimashou.com';

const canUseStagingSwitch = (currentUser) =>
  String(currentUser?.email || '').toLowerCase() === STAGING_SWITCH_DEVELOPER_EMAIL;

const filterStagingSwitchNavGroups = (groups, canShowStagingSwitch) =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => canShowStagingSwitch || item.href !== STAGING_DOMAIN_FALLBACK_HREF
      ),
    }))
    .filter((group) => group.items.length > 0);

const adminContentListApis = {
  '/admin/content/blog': fetchYowanAdminBlogList,
  '/admin/content/news': fetchYowanAdminNewsList,
};

const adminContentTitleColumnStyle = {
  width: '21rem',
  minWidth: '21rem',
};
const adminContentDateColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};
const adminContentActionColumnStyle = {
  width: '8.75rem',
  minWidth: '8.75rem',
  whiteSpace: 'nowrap',
};
const adminContentActionGroupStyle = {
  minWidth: '8.75rem',
  justifyContent: 'flex-start',
};
const adminContentPublishedAtColumnLabels = {
  '/admin/content/blog': '公開開始日時',
  '/admin/content/news': '配信予定日時',
};

const stackedFormFieldStyle = { gridTemplateColumns: '1fr' };
const fieldNoteStyle = { margin: 0, fontSize: '0.75rem', lineHeight: 1.45 };

const isPublicLocationChoice = (value) => value === true || value === 'true' || value === 'on';

const isLocalPreviewHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

export const appendYowanContentPreviewQuery = (target, currentSearch = '') => {
  const previewSearch = currentSearch || (isLocalPreviewHost() ? '?domain=yowan' : '');

  if (!previewSearch.includes('domain=yowan')) {
    return target;
  }

  if (typeof target !== 'string' || !target.startsWith('/')) {
    return target;
  }

  const hashIndex = target.indexOf('#');
  const hash = hashIndex >= 0 ? target.slice(hashIndex) : '';
  const pathAndSearch = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
  const searchIndex = pathAndSearch.indexOf('?');
  const pathname = searchIndex >= 0 ? pathAndSearch.slice(0, searchIndex) : pathAndSearch;
  const targetSearch = searchIndex >= 0 ? pathAndSearch.slice(searchIndex + 1) : '';
  const searchParams = new URLSearchParams(targetSearch);

  searchParams.set('domain', 'yowan');

  return `${pathname}?${searchParams.toString()}${hash}`;
};

const getSortableDateValue = (value) => {
  if (!value) {
    return 0;
  }

  const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
  const timestamp = Date.parse(normalized);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isFutureScheduleValue = (value) => {
  const timestamp = getSortableDateValue(value);

  return Boolean(timestamp && timestamp > Date.now());
};

const getAdminContentPublicationStatus = (row, contentKey) => {
  if (!row.isPublished) {
    return {
      label: contentKey === '/admin/content/news' ? '下書き' : '非公開',
      tone: 'draft',
    };
  }

  if (contentKey === '/admin/content/news') {
    if (isFutureScheduleValue(row.notificationScheduledAt)) {
      return { label: '配信予約', tone: 'review' };
    }

    if (row.notificationSentAt || row.notificationDispatchId || row.notificationDispatch) {
      return { label: '配信済み', tone: 'published' };
    }

    return { label: '配信待ち', tone: 'review' };
  }

  if (isFutureScheduleValue(row.publishedAt)) {
    return { label: '公開予約', tone: 'review' };
  }

  return { label: '公開中', tone: 'published' };
};

const formatAdminContentPublishedAt = (row, contentKey) => {
  if (contentKey === '/admin/content/news') {
    return formatYowanAdminDateTime(row.notificationScheduledAt);
  }

  return formatYowanAdminDateTime(row.publishedAt);
};

export const sortAdminContentRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'published-status') {
      if (left.isPublished === right.isPublished) {
        return left.title.localeCompare(right.title, 'ja');
      }

      return left.isPublished ? -1 : 1;
    }

    if (sortKey === 'title-asc') {
      return left.title.localeCompare(right.title, 'ja');
    }

    if (sortKey === 'title-desc') {
      return right.title.localeCompare(left.title, 'ja');
    }

    if (sortKey === 'published-desc') {
      return getSortableDateValue(right.publishedAt) - getSortableDateValue(left.publishedAt);
    }

    if (sortKey === 'slug-asc') {
      return left.slug.localeCompare(right.slug, 'ja');
    }

    return getSortableDateValue(right.updatedAt) - getSortableDateValue(left.updatedAt);
  });

  return nextRows;
};

export const usePreservedDomainSearch = () => {
  const previewSearch = useYowanPreviewSearch();

  if (previewSearch) {
    return previewSearch;
  }

  return isLocalPreviewHost() ? '?domain=yowan' : '';
};

export const sortAdminOrderedRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'published-status') {
      if (left.isPublished === right.isPublished) {
        return left.displayOrder - right.displayOrder;
      }

      return left.isPublished ? -1 : 1;
    }

    if (sortKey === 'title-asc') {
      return left.title.localeCompare(right.title, 'ja');
    }

    if (sortKey === 'title-desc') {
      return right.title.localeCompare(left.title, 'ja');
    }

    if (sortKey === 'display-order-desc') {
      return right.displayOrder - left.displayOrder;
    }

    return left.displayOrder - right.displayOrder;
  });

  return nextRows;
};

export const AdminPageHeader = ({ title, paragraphs, actions }) => {
  const relatedActionStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2rem',
    padding: '0.3rem 0.85rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(116, 126, 115, 0.16)',
    color: '#2f363d',
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.2,
    textDecoration: 'none',
    boxShadow: 'none',
  };
  const backActionStyle = {
    ...relatedActionStyle,
    background: 'rgba(89, 102, 116, 0.18)',
    color: '#24303a',
  };
  const normalizedActions = (actions || []).map((action, index) => {
    if (!React.isValidElement(action)) {
      return action;
    }

    const className = action.props.className || '';
    if (className.includes('c-form__submit')) {
      return action;
    }

    const actionLabel = typeof action.props.children === 'string'
      ? action.props.children.trim()
      : '';
    const actionStyle = actionLabel === '一覧へ戻る' ? backActionStyle : relatedActionStyle;

    return React.cloneElement(action, {
      style: {
        ...actionStyle,
        ...(action.props.style || {}),
      },
    });
  });

  return (
    <div className="yowan-admin-page-header">
      <Heading as="h1" align="left">
        {title}
      </Heading>
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="yowan-admin-page-header__lead">
          {paragraph}
        </p>
      ))}
      {normalizedActions.length ? (
        <div className="yowan-admin-page-header__actions">{normalizedActions}</div>
      ) : null}
    </div>
  );
};

export const AdminRelatedLinks = ({ links, preservedDomainSearch = '' }) => {
  if (!links?.length) {
    return null;
  }

  const sectionStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: '0.65rem',
    rowGap: '0.5rem',
    marginBlock: '0.75rem 1.25rem',
  };
  const labelStyle = {
    margin: 0,
    color: 'rgba(47, 54, 61, 0.72)',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  };
  const listStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  };
  const linkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2rem',
    padding: '0.3rem 0.85rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(116, 126, 115, 0.16)',
    color: '#2f363d',
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.2,
    textDecoration: 'none',
    boxShadow: 'none',
  };

  return (
    <div style={sectionStyle}>
      <p className="yowan-admin-page-header__lead" style={labelStyle}>
        関連するリンク
      </p>
      <div style={listStyle}>
        {links.map((item) => (
          <Link
            key={item.href}
            style={linkStyle}
            to={appendYowanContentPreviewQuery(item.href, preservedDomainSearch)}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const AdminBooleanToggleField = ({ label, register, name, helpText }) => (
  <div className="c-form__field yowan-admin-form__toggle-field">
    <span className="c-form__label">{label}</span>
    <label className="c-form__choice yowan-admin-form__toggle-choice">
      <input type="checkbox" {...register(name)} />
      <span>{label}</span>
    </label>
    {helpText ? (
      <p
        className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
      >
        {helpText}
      </p>
    ) : null}
  </div>
);

export const AdminCurrentImagePreview = ({ image = null }) => {
  const imageSrc = getYowanImagePreviewSrc(image);

  if (!imageSrc) {
    return null;
  }

  const imageLabel = getYowanImageDisplayName(image);

  return (
    <div className="yowan-admin-current-image">
      <img
        className="yowan-admin-current-image__thumbnail"
        src={imageSrc}
        alt={image?.alt_text || image?.altText || imageLabel || '現在の画像'}
      />
      <div className="yowan-admin-current-image__body">
        <p className="yowan-admin-current-image__label">現在の画像</p>
        <p className="yowan-admin-current-image__meta">新しい画像を選ぶと保存時に差し替わります。</p>
      </div>
    </div>
  );
};

const getEventOptionLabel = (options = [], value = '') => options.find((option) => option.value === value)?.label || '';

const getRoomOptionsForFacility = (rooms = [], facilityId = '', selectedRoomId = '') => {
  const selectedFacilityId = facilityId || '';
  const currentRoomId = selectedRoomId || '';
  const placeholder = rooms.find((option) => !option.value) || { value: '', label: '未選択' };

  if (!selectedFacilityId) {
    return [{ ...placeholder, label: '先に施設を選択' }];
  }

  const matchingRooms = rooms.filter((option) => (
    option.value && String(option.facilityId || '') === String(selectedFacilityId)
  ));
  const currentRoom = currentRoomId
    ? rooms.find((option) => String(option.value || '') === String(currentRoomId))
    : null;
  const shouldAppendCurrentRoom = currentRoom && !matchingRooms.some((option) => (
    String(option.value || '') === String(currentRoomId)
  ));
  const fallbackCurrentRoom = currentRoomId && !currentRoom
    ? { value: currentRoomId, label: '現在設定されている部屋（候補外・現在値）' }
    : null;

  return [
    placeholder,
    ...matchingRooms,
    ...(shouldAppendCurrentRoom ? [currentRoom] : []),
    ...(fallbackCurrentRoom ? [fallbackCurrentRoom] : []),
  ];
};

export const useAdminContentMediaOptions = (usageScope, currentAsset = null) => {
  const [mediaAssetOptions, setMediaAssetOptions] = useState([]);
  const [mediaAssetLoading, setMediaAssetLoading] = useState(false);
  const [mediaAssetError, setMediaAssetError] = useState('');

  useEffect(() => {
    if (!usageScope) {
      setMediaAssetOptions([]);
      return undefined;
    }

    let isMounted = true;
    setMediaAssetLoading(true);
    setMediaAssetError('');

    fetchYowanAdminContentMediaAssets({ usageScope })
      .then((assets) => {
        if (isMounted) {
          setMediaAssetOptions(buildContentMediaAssetOptions(assets, currentAsset));
        }
      })
      .catch(() => {
        if (isMounted) {
          setMediaAssetOptions(buildContentMediaAssetOptions([], currentAsset));
          setMediaAssetError('画像候補を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setMediaAssetLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [usageScope, currentAsset]);

  return {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  };
};

export const useBlogAuthorContentMediaOptions = (currentAsset = null) => {
  const [mediaAssetOptions, setMediaAssetOptions] = useState([]);
  const [mediaAssetLoading, setMediaAssetLoading] = useState(false);
  const [mediaAssetError, setMediaAssetError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setMediaAssetLoading(true);
    setMediaAssetError('');

    fetchYowanBlogAuthorMediaAssets()
      .then((assets) => {
        if (isMounted) {
          setMediaAssetOptions(buildContentMediaAssetOptions(assets, currentAsset));
        }
      })
      .catch(() => {
        if (isMounted) {
          setMediaAssetOptions(buildContentMediaAssetOptions([], currentAsset));
          setMediaAssetError('画像候補を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setMediaAssetLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentAsset]);

  return {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  };
};

export const useAdminBlogAuthorOptions = (currentAuthor = null) => {
  const [authorOptions, setAuthorOptions] = useState([]);
  const [authorOptionsLoading, setAuthorOptionsLoading] = useState(false);
  const [authorOptionsError, setAuthorOptionsError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setAuthorOptionsLoading(true);
    setAuthorOptionsError('');

    fetchYowanAdminBlogAuthorAccessList({ page: 1, pageSize: 100, isActive: 'true' })
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        const options = [
          { value: '', label: '著者を指定しない' },
          ...payload.results
            .filter((access) => access.authorId || access.authorName)
            .map((access) => ({
              value: access.authorId,
              label: access.authorName
                ? `${access.authorName}${access.email ? ` / ${access.email}` : ''}`
                : access.email,
            })),
        ];

        if (currentAuthor?.id && !options.some((option) => option.value === currentAuthor.id)) {
          options.push({
            value: currentAuthor.id,
            label: currentAuthor.displayName || currentAuthor.name || '選択中の著者',
          });
        }

        setAuthorOptions(options);
      })
      .catch(() => {
        if (isMounted) {
          setAuthorOptions([
            { value: '', label: '著者を指定しない' },
          ]);
          setAuthorOptionsError('著者候補を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setAuthorOptionsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentAuthor]);

  return {
    authorOptions,
    authorOptionsLoading,
    authorOptionsError,
  };
};

const AdminMediaAssetSelectField = ({
  content,
  register,
  mediaAssetOptions,
  mediaAssetLoading = false,
  mediaAssetError = '',
  currentAsset = null,
  errors = {},
}) => {
  const mediaAssetField = content.form.mediaAssetField;

  if (!mediaAssetField) {
    return null;
  }

  const options = mediaAssetOptions || mediaAssetField.options || [];
  const label = content.form.labels.primaryImage || content.form.labels.image || '画像';
  const currentOption = options.find((option) => option.isCurrent);
  const currentPreviewAsset = currentAsset || (currentOption ? {
    preview_url: currentOption.preview_url,
    thumbnail_url: currentOption.thumbnail_url,
    url: currentOption.url,
    alt_text: currentOption.description,
    caption: currentOption.label,
    fileName: currentOption.fileName,
  } : null);

  return (
    <div className="c-form__field" style={stackedFormFieldStyle}>
      <span className="c-form__label">{label}</span>
      <div
        style={{
          display: 'grid',
          gap: '0.5rem',
          minWidth: 0,
        }}
      >
        <AdminCurrentImagePreview image={currentPreviewAsset} />
        {options.length ? (
          <select
            className="c-form__control"
            data-api-path={mediaAssetField.api?.path}
            {...register(mediaAssetField.name)}
          >
            <option value="">既存画像を使わない</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {option.isActive === false ? ' / 停止中' : ''}
              </option>
            ))}
          </select>
        ) : (
          <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
            登録済み画像はありません。
          </p>
        )}
        <input
          className="c-form__control"
          type="file"
          accept="image/*"
          {...register('primary_image_upload', { validate: validateYowanImageUploadFile })}
        />
        {errors?.primary_image_upload ? (
          <em className="yowan-admin-form__error" style={{ gridColumn: 1 }}>
            {errors.primary_image_upload.message}
          </em>
        ) : null}
        <input
          className="c-form__control"
          type="text"
          placeholder="代替テキスト"
          {...register('primary_image_alt_text')}
        />
        <input
          className="c-form__control"
          type="text"
          placeholder="キャプション"
          {...register('primary_image_caption')}
        />
      </div>
      {mediaAssetField.helpText || YOWAN_IMAGE_UPLOAD_HELP_TEXT ? (
        <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
          {[mediaAssetField.helpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}
        </p>
      ) : null}
      {mediaAssetLoading || mediaAssetError ? (
        <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
          {mediaAssetLoading ? <LoadingSpinner inline size="sm" /> : mediaAssetError}
        </p>
      ) : null}
      {options.length ? (
        <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
          {options.map((option) => option.description).filter(Boolean).join(' / ')}
        </p>
      ) : null}
    </div>
  );
};

export const AdminContentEditorFields = ({
  content,
  register,
  errors,
  requireTitleAndSlug = true,
  showSlugField = true,
  slugRequired = requireTitleAndSlug,
  slugReadOnly = false,
  showPublishControls = true,
  showNotifyOnPublishField = true,
  showMediaFields = true,
  authorOptions,
  authorOptionsLoading = false,
  authorOptionsError = '',
  mediaAssetOptions,
  mediaAssetLoading,
  mediaAssetError,
  currentImage,
}) => {
  const excerptMaxLength = content.form.excerptMaxLength || undefined;
  const publishedAtInputType = content.form.publishedAtInputType || 'datetime-local';
  const publishedAtRules = content.form.publishedAtRequired === false
    ? {}
    : { required: content.form.errors.required };
  const notificationScheduledAtRules = content.form.notificationScheduledAtRequired === false
    ? {}
    : { required: content.form.errors.required };
  const slugValidationRules = {
    ...(slugRequired && !slugReadOnly ? { required: content.form.errors.required } : {}),
    ...(!slugReadOnly
      ? {
          pattern: {
            value: /^[A-Za-z0-9_-]*$/,
            message:
              content.form.errors.invalidSlug ||
              'slugは半角英数字、ハイフン、アンダースコアで入力してください。',
          },
        }
      : {}),
  };

  return (
    <>
      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.title}
          {requireTitleAndSlug ? <em className="c-form__required">*</em> : null}
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register(
            'title',
            requireTitleAndSlug ? { required: content.form.errors.required } : {},
          )}
        />
        {errors?.title ? <em className="yowan-admin-form__error">{errors.title.message}</em> : null}
      </label>

      {showSlugField ? (
        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.slug}
            {slugRequired && !slugReadOnly ? <em className="c-form__required">*</em> : null}
          </span>
          <input
            className="c-form__control"
            type="text"
            readOnly={slugReadOnly}
            style={slugReadOnly ? { cursor: 'not-allowed' } : undefined}
            {...register('slug', slugValidationRules)}
          />
          {errors?.slug ? <em className="yowan-admin-form__error">{errors.slug.message}</em> : null}
          {slugReadOnly || content.form.slugHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {slugReadOnly
                ? content.form.slugReadOnlyHelpText || 'slug は作成時のみ設定でき、公開URLとSEOの安定性を守るため編集できません。'
                : content.form.slugHelpText}
            </p>
          ) : null}
        </label>
      ) : null}

      {content.form.authorField ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.author}</span>
          <select className="c-form__control" {...register('authorId')}>
            {(authorOptions || content.form.authorField.options || []).map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {authorOptionsLoading || authorOptionsError ? (
            <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
              {authorOptionsLoading ? '著者候補を読み込み中です。' : authorOptionsError}
            </p>
          ) : content.form.authorHelpText ? (
            <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
              {content.form.authorHelpText}
            </p>
          ) : null}
        </label>
      ) : null}

      {showPublishControls ? (
        <AdminBooleanToggleField
          label={content.form.labels.isPublished}
          name="isPublished"
          register={register}
        />
      ) : null}

      {showNotifyOnPublishField && content.form.labels.notifyOnPublish ? (
        <AdminBooleanToggleField
          label={content.form.labels.notifyOnPublish}
          name="notify_on_publish"
          register={register}
          helpText={content.form.notifyOnPublishHelpText}
        />
      ) : null}

      {!showPublishControls || content.form.showPublishedAtField === false ? null : (
        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.publishedAt}
            {content.form.publishedAtRequired === false ? null : <em className="c-form__required">*</em>}
          </span>
          <input className="c-form__control" type={publishedAtInputType} {...register('publishedAt', publishedAtRules)} />
          {errors?.publishedAt ? <em className="yowan-admin-form__error">{errors.publishedAt.message}</em> : null}
          {content.form.publishedAtHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {content.form.publishedAtHelpText}
            </p>
          ) : null}
        </label>
      )}

      {content.form.labels.notificationScheduledAt ? (
        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.notificationScheduledAt}
            {content.form.notificationScheduledAtRequired === false ? null : <em className="c-form__required">*</em>}
          </span>
          <input
            className="c-form__control"
            type="datetime-local"
            {...register('notificationScheduledAt', notificationScheduledAtRules)}
          />
          {errors?.notificationScheduledAt ? (
            <em className="yowan-admin-form__error">{errors.notificationScheduledAt.message}</em>
          ) : null}
          {content.form.notificationScheduledAtHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {content.form.notificationScheduledAtHelpText}
            </p>
          ) : null}
        </label>
      ) : null}

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.excerpt}</span>
        <textarea
          className="c-form__control c-form__control--textarea"
          rows="4"
          maxLength={excerptMaxLength}
          {...register('excerpt', excerptMaxLength ? {
            maxLength: {
              value: excerptMaxLength,
              message: `${excerptMaxLength}文字以内で入力してください。`,
            },
          } : {})}
        />
        {errors?.excerpt ? <em className="yowan-admin-form__error">{errors.excerpt.message}</em> : null}
        {content.form.excerptHelpText ? (
          <p
            className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
          >
            {content.form.excerptHelpText}
          </p>
        ) : null}
      </label>

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.body}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control c-form__control--textarea"
          rows="12"
          {...register('body', { required: content.form.errors.required })}
        />
        {errors?.body ? <em className="yowan-admin-form__error">{errors.body.message}</em> : null}
        {content.form.bodyHelpText ? (
          <p
            className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
          >
            {content.form.bodyHelpText}
          </p>
        ) : null}
      </label>

      {showMediaFields && content.form.labels.image ? (
        <div className="c-form__field">
          <span className="c-form__label">{content.form.labels.image}</span>
          <input
            className="c-form__control"
            type="file"
            accept="image/*"
            {...register('image', { validate: validateYowanImageUploadFile })}
          />
          {errors?.image ? <em className="yowan-admin-form__error">{errors.image.message}</em> : null}
          {content.form.imageHelpText || YOWAN_IMAGE_UPLOAD_HELP_TEXT ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {[content.form.imageHelpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}
            </p>
          ) : null}
        </div>
      ) : null}
      {showMediaFields && content.form.mediaAssetField ? (
        <AdminMediaAssetSelectField
          content={content}
          register={register}
          errors={errors}
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
          currentAsset={currentImage}
        />
      ) : null}
    </>
  );
};

export const AdminReviewEditorFields = ({
  content,
  register,
  errors,
  mediaAssetOptions,
  mediaAssetLoading,
  mediaAssetError,
  currentImage,
}) => {
  return (
    <>
      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.title}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('title', { required: content.form.errors.required })}
        />
        {errors?.title ? <em className="yowan-admin-form__error">{errors.title.message}</em> : null}
      </label>

      {content.form.labels.reviewerName ? (
        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.reviewerName}
            <em className="c-form__required">*</em>
          </span>
          <input
            className="c-form__control"
            type="text"
            {...register('reviewerName', { required: content.form.errors.required })}
          />
          {errors?.reviewerName ? <em className="yowan-admin-form__error">{errors.reviewerName.message}</em> : null}
        </label>
      ) : null}

      {content.form.labels.reviewerTitle ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.reviewerTitle}</span>
          <input
            className="c-form__control"
            type="text"
            {...register('reviewerTitle')}
          />
        </label>
      ) : null}

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.displayOrder}
          <em className="c-form__required">*</em>
        </span>
        <div style={{ display: 'grid', gap: '0.5rem', minWidth: 0 }}>
          <input
            className="c-form__control"
            type="number"
            min="1"
            step="1"
            {...register('displayOrder', {
              required: content.form.errors.required,
              valueAsNumber: true,
            })}
          />
          {errors?.displayOrder ? (
            <em className="yowan-admin-form__error">{errors.displayOrder.message}</em>
          ) : null}
          {content.form.displayOrderHelpText ? (
            <p
              className="yowan-admin-form__field-note"
              style={fieldNoteStyle}
            >
              {content.form.displayOrderHelpText}
            </p>
          ) : null}
        </div>
      </label>

      <AdminBooleanToggleField
        label={content.form.labels.isPublished}
        name="isPublished"
        register={register}
      />

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.body}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control c-form__control--textarea"
          rows="12"
          {...register('body', { required: content.form.errors.required })}
        />
        {errors?.body ? <em className="yowan-admin-form__error">{errors.body.message}</em> : null}
      </label>

      {content.form.labels.image ? (
        <div className="c-form__field">
          <span className="c-form__label">{content.form.labels.image}</span>
          <input
            className="c-form__control"
            type="file"
            accept="image/*"
            {...register('image', { validate: validateYowanImageUploadFile })}
          />
          {errors?.image ? <em className="yowan-admin-form__error">{errors.image.message}</em> : null}
          {content.form.imageHelpText || YOWAN_IMAGE_UPLOAD_HELP_TEXT ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {[content.form.imageHelpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}
            </p>
          ) : null}
        </div>
      ) : null}
      {content.form.mediaAssetField ? (
        <AdminMediaAssetSelectField
          content={content}
          register={register}
          errors={errors}
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
          currentAsset={currentImage}
        />
      ) : null}
    </>
  );
};

export const AdminMemberEditorFields = ({ content, register, errors }) => {
  return (
    <>
      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.name}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('name', { required: content.form.errors.required })}
        />
        {errors?.name ? <em className="yowan-admin-form__error">{errors.name.message}</em> : null}
      </label>

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.nameKana}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('nameKana', { required: content.form.errors.required })}
        />
        {errors?.nameKana ? <em className="yowan-admin-form__error">{errors.nameKana.message}</em> : null}
      </label>

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.email}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="email"
          {...register('email', { required: content.form.errors.required })}
        />
        {errors?.email ? <em className="yowan-admin-form__error">{errors.email.message}</em> : null}
      </label>

      {content.form.labels.password ? (
        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.password}
            <em className="c-form__required">*</em>
          </span>
          <input
            className="c-form__control"
            type="password"
            autoComplete="new-password"
            {...register('password', { required: content.form.errors.required })}
          />
          {errors?.password ? <em className="yowan-admin-form__error">{errors.password.message}</em> : null}
        </label>
      ) : null}

      {content.form.labels.sendPasswordSetup ? (
        <AdminBooleanToggleField
          label={content.form.labels.sendPasswordSetup}
          name="sendPasswordSetup"
          register={register}
          helpText={content.form.sendPasswordSetupHelpText}
        />
      ) : null}

      {content.form.labels.isActive ? (
        <AdminBooleanToggleField
          label={content.form.labels.isActive}
          name="isActive"
          register={register}
          helpText={content.form.isActiveHelpText}
        />
      ) : null}

      <div className="c-form__field">
        <span className="c-form__label">{content.form.labels.lifecycleStatus}</span>
        <select className="c-form__control" {...register('lifecycleStatus')}>
          {content.form.options.lifecycleStatus.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {content.form.lifecycleStatusHelpText ? (
          <p
            className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
          >
            {content.form.lifecycleStatusHelpText}
          </p>
        ) : null}
      </div>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.phone}</span>
        <input className="c-form__control" type="text" {...register('phone')} />
      </label>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.postalCode}</span>
        <input className="c-form__control" type="text" {...register('postalCode')} />
      </label>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.address}</span>
        <textarea className="c-form__control c-form__control--textarea" rows="3" {...register('address')} />
      </label>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.note}</span>
        <textarea className="c-form__control c-form__control--textarea" rows="4" {...register('note')} />
      </label>

      {content.form.labels.profileJson ? (
        <div className="c-form__field">
          <span className="c-form__label">{content.form.labels.profileJson}</span>
          <textarea
            className="c-form__control c-form__control--textarea"
            rows="6"
            {...register('profileJson', {
              validate: (value) => {
                if (!value || !value.trim()) {
                  return true;
                }

                try {
                  JSON.parse(value);
                  return true;
                } catch (error) {
                  return content.form.errors.invalidJson;
                }
              },
            })}
          />
          {errors?.profileJson ? <em className="yowan-admin-form__error">{errors.profileJson.message}</em> : null}
          {content.form.profileJsonHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {content.form.profileJsonHelpText}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export const AdminApplicantEditorFields = ({ content, register, errors }) => {
  return (
    <>
      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.name}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('name', { required: content.form.errors.required })}
        />
        {errors?.name ? <em className="yowan-admin-form__error">{errors.name.message}</em> : null}
      </label>

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.email}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="email"
          {...register('email', { required: content.form.errors.required })}
        />
        {errors?.email ? <em className="yowan-admin-form__error">{errors.email.message}</em> : null}
      </label>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.nameKana}</span>
        <input className="c-form__control" type="text" {...register('nameKana')} />
      </label>

      <div className="c-form__field">
        <span className="c-form__label">{content.form.labels.gender}</span>
        <select className="c-form__control" {...register('gender')}>
          {content.form.options.gender.map((option) => (
            <option key={option.value || 'blank'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.age}</span>
        <input className="c-form__control" type="number" min="0" step="1" {...register('age')} />
      </label>

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.phone}</span>
        <input className="c-form__control" type="text" {...register('phone')} />
      </label>

      <div className="c-form__field">
        <span className="c-form__label">{content.form.labels.applicantType}</span>
        <select className="c-form__control" {...register('applicantType')}>
          {content.form.options.applicantType.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <AdminBooleanToggleField
        label={content.form.labels.isActive}
        name="isActive"
        register={register}
        helpText={content.form.isActiveHelpText}
      />

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.note}</span>
        <textarea className="c-form__control c-form__control--textarea" rows="4" {...register('note')} />
      </label>
    </>
  );
};

export const AdminEventEditorFields = ({
  content,
  register,
  errors,
  setValue,
  watch,
  slotCount = 0,
  slotIndexes: providedSlotIndexes,
  onAddSlot,
  onRemoveSlot,
  currentImage,
  currentDetailTopImage,
  hidePublicSlug = false,
  publicSlugDisabled = false,
  publicPathPreview = '',
  canonicalUrlPreview = '',
  publicSlugNote = '',
  renderAfterPublished = null,
}) => {
  const slotLabels = content.form.slotLabels;
  const scheduleLabels = content.form.scheduleLabels;
  const summaryMaxLength = content.form.summaryMaxLength || undefined;
  const guidanceMaxLength = content.form.guidanceMaxLength || undefined;
  const slotIndexes = providedSlotIndexes || Array.from({ length: slotCount }, (_, index) => index);
  const watchedStandardFacilityId = watch ? watch('standardFacilityId') : '';
  const watchedStandardRoomId = watch ? watch('standardRoomId') : '';
  const watchedScheduleValues = watch ? watch('schedules') : null;
  const watchedSchedules = useMemo(
    () => (scheduleLabels && Array.isArray(watchedScheduleValues) ? watchedScheduleValues : []),
    [scheduleLabels, watchedScheduleValues],
  );
  const watchedSlotValues = watch ? watch('slots') : null;
  const watchedSlots = useMemo(
    () => (slotLabels && Array.isArray(watchedSlotValues) ? watchedSlotValues : []),
    [slotLabels, watchedSlotValues],
  );
  const autoScheduleLabelRef = useRef({});
  const slotSectionStyle = {
    display: 'grid',
    gap: '0.85rem',
    padding: '1rem',
    borderRadius: '1rem',
    background: 'rgba(89, 102, 116, 0.08)',
  };
  const slotHeaderStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'start',
    justifyItems: 'start',
    gap: '0.65rem',
  };
  const slotTitleStyle = {
    margin: 0,
    color: '#24303a',
    fontSize: '1rem',
    fontWeight: 700,
  };
  const slotLeadStyle = {
    margin: 0,
    color: 'rgba(47, 54, 61, 0.72)',
    fontSize: '0.85rem',
    lineHeight: 1.55,
  };
  const slotButtonStyle = {
    justifySelf: 'start',
    minHeight: '2rem',
    padding: '0.32rem 0.9rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(116, 126, 115, 0.16)',
    color: '#2f363d',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  };
  const slotCardStyle = {
    display: 'grid',
    gap: '0.7rem',
    padding: '0.85rem',
    border: '1px solid rgba(89, 102, 116, 0.14)',
    borderRadius: '0.85rem',
    background: 'rgba(255, 255, 255, 0.68)',
  };
  const slotCardHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  };
  const slotRemoveButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.8rem',
    height: '1.8rem',
    border: 'none',
    borderRadius: '999px',
    background: 'rgba(124, 42, 46, 0.12)',
    color: '#7c2a2e',
    cursor: 'pointer',
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1,
  };
  const slotGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.7rem',
  };
  const renderFieldNote = (text, style = {}) => {
    if (!text) {
      return null;
    }

    return (
      <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact" style={style}>
        {text}
      </p>
    );
  };
  const renderEventImageField = ({
    label,
    idFieldName,
    fileFieldName,
    currentAsset,
    helpText,
  }) => {
    if (!label) {
      return null;
    }

    return (
      <div className="c-form__field">
        <span className="c-form__label">{label}</span>
        <input type="hidden" {...register(idFieldName)} />
        <AdminCurrentImagePreview image={currentAsset} />
        <input
          className="c-form__control"
          type={content.form.imageInputType === 'file' ? 'file' : 'url'}
          accept={content.form.imageInputType === 'file' ? 'image/*' : undefined}
          {...register(
            fileFieldName,
            content.form.imageInputType === 'file'
              ? { validate: validateYowanImageUploadFile }
              : {},
          )}
        />
        {errors?.[fileFieldName] ? (
          <em className="yowan-admin-form__error">{errors[fileFieldName].message}</em>
        ) : null}
        {renderFieldNote(
          content.form.imageInputType === 'file'
            ? [helpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')
            : helpText,
        )}
      </div>
    );
  };
  const buildScheduleAutoLabel = useCallback((schedule = {}) => {
    const scheduleKind = schedule.scheduleKind || 'weekly';
    const weekday = schedule.weekday || '';
    const kindLabel = getEventOptionLabel(content.form.options.scheduleKinds || [], scheduleKind);
    const weekdayLabel = getEventOptionLabel(content.form.options.weekday || [], weekday);

    if (scheduleKind === 'custom') {
      return weekdayLabel || '';
    }

    if (scheduleKind === 'weekly' || scheduleKind === 'biweekly') {
      return [kindLabel, weekdayLabel].filter(Boolean).join('');
    }

    if (scheduleKind === 'monthly_nth') {
      return weekdayLabel ? `毎月第n${weekdayLabel}` : kindLabel;
    }

    return [kindLabel, weekdayLabel].filter(Boolean).join(' ');
  }, [content.form.options.scheduleKinds, content.form.options.weekday]);

  useEffect(() => {
    if (!scheduleLabels || !setValue || !Array.isArray(watchedSchedules)) {
      return;
    }

    watchedSchedules.forEach((schedule, schedulePosition) => {
      const nextLabel = buildScheduleAutoLabel(schedule);
      const currentLabel = schedule?.scheduleLabel || '';
      const previousAutoLabel = autoScheduleLabelRef.current[schedulePosition] || '';

      if (!nextLabel) {
        return;
      }

      if (currentLabel === nextLabel) {
        autoScheduleLabelRef.current[schedulePosition] = nextLabel;
        return;
      }

      if (!currentLabel || currentLabel === previousAutoLabel) {
        setValue(`schedules.${schedulePosition}.scheduleLabel`, nextLabel, {
          shouldDirty: Boolean(currentLabel),
          shouldValidate: false,
        });
        autoScheduleLabelRef.current[schedulePosition] = nextLabel;
      }
    });
  }, [buildScheduleAutoLabel, scheduleLabels, watchedSchedules, setValue]);
  const updateScheduleAutoLabel = useCallback((schedulePosition, overrides = {}) => {
    if (!setValue || !watch) {
      return;
    }

    const currentSchedule = watch(`schedules.${schedulePosition}`) || {};
    const nextSchedule = {
      ...currentSchedule,
      ...overrides,
    };
    const nextLabel = buildScheduleAutoLabel(nextSchedule);
    const currentLabel = currentSchedule.scheduleLabel || '';
    const previousAutoLabel = autoScheduleLabelRef.current[schedulePosition] || '';

    if (!nextLabel) {
      return;
    }

    if (!currentLabel || currentLabel === previousAutoLabel) {
      setValue(`schedules.${schedulePosition}.scheduleLabel`, nextLabel, {
        shouldDirty: Boolean(currentLabel),
        shouldValidate: false,
      });
      autoScheduleLabelRef.current[schedulePosition] = nextLabel;
    }
  }, [buildScheduleAutoLabel, setValue, watch]);

  return (
    <>
      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.title}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('title', { required: content.form.errors.required })}
        />
        {errors?.title ? <em className="yowan-admin-form__error">{errors.title.message}</em> : null}
      </label>

      {!hidePublicSlug && content.form.labels.publicSlug ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.publicSlug}</span>
          <input
            className="c-form__control"
            type="text"
            disabled={publicSlugDisabled}
            {...register('publicSlug')}
          />
          {renderFieldNote(publicSlugNote || content.form.publicSlugHelpText)}
          {publicPathPreview || canonicalUrlPreview ? (
            <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
              公開URL: {publicPathPreview || '-'} / canonical: {canonicalUrlPreview || '-'}
            </p>
          ) : null}
          {errors?.publicSlug ? <em className="yowan-admin-form__error">{errors.publicSlug.message}</em> : null}
        </label>
      ) : null}

      {content.form.labels.subtitle ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.subtitle}</span>
          <input
            className="c-form__control"
            type="text"
            {...register('subtitle')}
          />
          {renderFieldNote(content.form.subtitleHelpText)}
        </label>
      ) : null}

      <div className="c-form__field">
        <span className="c-form__label">{content.form.labels.eventType}</span>
        <select className="c-form__control" disabled={content.form.eventTypeLocked} {...register('eventType')}>
          {content.form.options.eventType.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {content.form.labels.organizerId ? (
        <div className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.organizerId}
            <em className="c-form__required">*</em>
          </span>
          <select
            className="c-form__control"
            {...register('organizerId', { required: content.form.errors.required })}
          >
            {(content.form.options.organizers || []).map((option) => (
              <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                {option.label}
              </option>
            ))}
          </select>
          {errors?.organizerId ? (
            <em className="yowan-admin-form__error">{errors.organizerId.message}</em>
          ) : null}
          {content.form.organizerHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {content.form.organizerHelpText}
            </p>
          ) : null}
        </div>
      ) : null}

      {content.form.labels.weekday && !content.form.standardSlotLabels && !scheduleLabels ? (
        <div className="c-form__field">
          <span className="c-form__label">{content.form.labels.weekday}</span>
          <select className="c-form__control" {...register('weekday')}>
            {content.form.options.weekday.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <AdminBooleanToggleField
        label={content.form.labels.isPublished}
        name="isPublished"
        register={register}
        helpText={content.form.isPublishedHelpText}
      />
      {typeof renderAfterPublished === 'function' ? renderAfterPublished() : null}

      {content.form.labels.isActive ? (
        <AdminBooleanToggleField
          label={content.form.labels.isActive}
          name="isActive"
          register={register}
          helpText={content.form.isActiveHelpText}
        />
      ) : null}

      {content.form.labels.showInstructorProfiles ? (
        <AdminBooleanToggleField
          label={content.form.labels.showInstructorProfiles}
          name="showInstructorProfiles"
          register={register}
          helpText={content.form.showInstructorProfilesHelpText}
        />
      ) : null}

      {content.form.labels.calendarUrl ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.calendarUrl}</span>
          <input className="c-form__control" type="url" {...register('calendarUrl')} />
          {renderFieldNote(content.form.calendarUrlHelpText)}
        </label>
      ) : null}

      <label className="c-form__field">
        <span className="c-form__label">{content.form.labels.summary}</span>
        <textarea
          className="c-form__control c-form__control--textarea"
          rows="3"
          maxLength={summaryMaxLength}
          {...register('summary', summaryMaxLength ? {
            maxLength: {
              value: summaryMaxLength,
              message: `${summaryMaxLength}文字以内で入力してください。`,
            },
          } : {})}
        />
        {renderFieldNote(content.form.summaryHelpText)}
        {errors?.summary ? <em className="yowan-admin-form__error">{errors.summary.message}</em> : null}
      </label>

      {content.form.labels.fullParticipationFee ? (
        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.fullParticipationFee}</span>
          <input
            className="c-form__control"
            type="number"
            min="0"
            step="100"
            {...register('fullParticipationFee')}
          />
          {renderFieldNote(content.form.fullParticipationFeeHelpText)}
        </label>
      ) : null}

      <label className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.guidance}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control c-form__control--textarea"
          rows="8"
          maxLength={guidanceMaxLength}
          {...register('guidance', {
            required: content.form.errors.required,
            ...(guidanceMaxLength ? {
              maxLength: {
                value: guidanceMaxLength,
                message: `${guidanceMaxLength}文字以内で入力してください。`,
              },
            } : {}),
          })}
        />
        {renderFieldNote(content.form.guidanceHelpText)}
        {errors?.guidance ? <em className="yowan-admin-form__error">{errors.guidance.message}</em> : null}
      </label>

      {content.form.labels.scheduleRuleJson ? (
        <div className="c-form__field">
          <span className="c-form__label">{content.form.labels.scheduleRuleJson}</span>
          <textarea className="c-form__control c-form__control--textarea" rows="6" {...register('scheduleRuleJson')} />
          {content.form.scheduleRuleJsonHelpText ? (
            <p
              className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
            >
              {content.form.scheduleRuleJsonHelpText}
            </p>
          ) : null}
        </div>
      ) : null}

      {renderEventImageField({
        label: content.form.labels.detailTopImage,
        idFieldName: 'detail_top_image_id',
        fileFieldName: 'detailTopImage',
        currentAsset: currentDetailTopImage,
        helpText: content.form.detailTopImageHelpText,
      })}

      {renderEventImageField({
        label: content.form.labels.image,
        idFieldName: 'primary_image_id',
        fileFieldName: 'image',
        currentAsset: currentImage,
        helpText: content.form.imageHelpText,
      })}

      {content.form.standardSlotLabels ? (
        <>
          <input type="hidden" {...register('standardScheduleId')} />

          {content.form.standardSlotLabels.scheduleKind ? (
            <div className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.scheduleKind}</span>
              <select className="c-form__control" {...register('standardScheduleKind')}>
                {(content.form.options.scheduleKinds || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {renderFieldNote(content.form.scheduleKindHelpText)}
            </div>
          ) : null}

          {content.form.standardSlotLabels.scheduleLabel ? (
            <label className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.scheduleLabel}</span>
              <input className="c-form__control" type="text" {...register('standardScheduleLabel')} />
              {renderFieldNote(content.form.scheduleLabelHelpText)}
            </label>
          ) : null}

          {content.form.standardSlotLabels.weekday ? (
            <div className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.weekday}</span>
              <select className="c-form__control" {...register('weekday')}>
                {(content.form.options.weekday || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.standardSlotLabels.startAt}
              <em className="c-form__required">*</em>
            </span>
            <input
              className="c-form__control"
              type="time"
              {...register('standardStartAt', { required: content.form.errors.required })}
            />
            {errors?.standardStartAt ? (
              <em className="yowan-admin-form__error">{errors.standardStartAt.message}</em>
            ) : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.standardSlotLabels.endAt}
              <em className="c-form__required">*</em>
            </span>
            <input
              className="c-form__control"
              type="time"
              {...register('standardEndAt', { required: content.form.errors.required })}
            />
            {errors?.standardEndAt ? (
              <em className="yowan-admin-form__error">{errors.standardEndAt.message}</em>
            ) : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.standardSlotLabels.facility}
            </span>
            <select
              className="c-form__control"
              {...register('standardFacilityId', {
                onChange: () => setValue?.('standardRoomId', '', { shouldDirty: true, shouldValidate: true }),
              })}
            >
              {(content.form.options.facilities || []).map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled === true}>
                  {option.label}
                </option>
              ))}
            </select>
            {renderFieldNote(content.form.facilityHelpText)}
            {errors?.standardFacilityId ? (
              <em className="yowan-admin-form__error">{errors.standardFacilityId.message}</em>
            ) : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.standardSlotLabels.room}
            </span>
            <select
              className="c-form__control"
              disabled={!watchedStandardFacilityId}
              {...register('standardRoomId')}
            >
              {getRoomOptionsForFacility(content.form.options.rooms || [], watchedStandardFacilityId, watchedStandardRoomId).map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled === true}>
                  {option.label}
                </option>
              ))}
            </select>
            {renderFieldNote(content.form.roomHelpText)}
            {errors?.standardRoomId ? (
              <em className="yowan-admin-form__error">{errors.standardRoomId.message}</em>
            ) : null}
          </label>

          {content.form.standardSlotLabels.locationDisplayMode ? (
            <div className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.locationDisplayMode}</span>
              <select className="c-form__control" {...register('standardLocationIsPublic')}>
                {(content.form.options.locationDisplayModes || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {renderFieldNote(content.form.locationDisplayModeHelpText)}
            </div>
          ) : null}

          {content.form.standardSlotLabels.publicLocationLabel ? (
            <label className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.publicLocationLabel}</span>
              <input className="c-form__control" type="text" {...register('standardPublicLocationLabel')} />
              {renderFieldNote(content.form.publicLocationLabelHelpText)}
            </label>
          ) : null}

          {content.form.standardSlotLabels.publicLocationNote ? (
            <label className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.publicLocationNote}</span>
              <textarea className="c-form__control c-form__control--textarea" rows="3" {...register('standardPublicLocationNote')} />
              {renderFieldNote(content.form.publicLocationNoteHelpText)}
            </label>
          ) : null}

          {content.form.standardSlotLabels.privateLocationNote ? (
            <label className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.privateLocationNote}</span>
              <textarea className="c-form__control c-form__control--textarea" rows="3" {...register('standardPrivateLocationNote')} />
              {renderFieldNote(content.form.privateLocationNoteHelpText)}
            </label>
          ) : null}

          <label className="c-form__field">
            <span className="c-form__label">{content.form.standardSlotLabels.capacity}</span>
            <input className="c-form__control" type="number" min="0" step="1" {...register('standardCapacity')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{content.form.standardSlotLabels.slotFee}</span>
            <input className="c-form__control" type="number" min="0" step="100" {...register('standardSlotFee')} />
          </label>

          <label className="c-form__field">
            <span className="c-form__label">
              {content.form.standardSlotLabels.instructor}
              <em className="c-form__required">*</em>
            </span>
            <select
              className="c-form__control"
              {...register('standardInstructorId', { required: content.form.errors.required })}
            >
              {(content.form.options.instructors || []).map((option) => (
                <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors?.standardInstructorId ? (
              <em className="yowan-admin-form__error">{errors.standardInstructorId.message}</em>
            ) : null}
          </label>

          {content.form.standardSlotLabels.isActive ? (
            <AdminBooleanToggleField
              label={content.form.standardSlotLabels.isActive}
              name="standardIsActive"
              register={register}
            />
          ) : null}

          {content.form.standardSlotLabels.remarks ? (
            <label className="c-form__field">
              <span className="c-form__label">{content.form.standardSlotLabels.remarks}</span>
              <textarea className="c-form__control c-form__control--textarea" rows="3" {...register('standardRemarks')} />
            </label>
          ) : null}
        </>
      ) : null}

      {scheduleLabels ? (
        <section className="yowan-admin-event-slots" style={slotSectionStyle}>
          <div style={slotHeaderStyle}>
            <div>
              <h2 style={slotTitleStyle}>{scheduleLabels.sectionTitle}</h2>
              <p style={slotLeadStyle}>{scheduleLabels.sectionLead}</p>
            </div>
            <button type="button" style={slotButtonStyle} onClick={onAddSlot}>
              + {scheduleLabels.addButton}
            </button>
          </div>

          {content.form.scheduleHelpText ? (
            <p className="yowan-admin-form__field-note" style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.45 }}>
              {content.form.scheduleHelpText}
            </p>
          ) : null}

          {slotIndexes.map((scheduleIndex, schedulePosition) => (
            <div key={scheduleIndex} className="yowan-admin-event-slots__card" style={slotCardStyle}>
              <input type="hidden" {...register(`schedules.${schedulePosition}.id`)} />
              <div style={slotCardHeaderStyle}>
                <h3 style={slotTitleStyle}>{scheduleLabels.itemTitle} {schedulePosition + 1}</h3>
                {slotIndexes.length > 1 ? (
                  <button
                    type="button"
                    aria-label={`${scheduleLabels.itemTitle} ${schedulePosition + 1} を削除`}
                    style={slotRemoveButtonStyle}
                    onClick={() => onRemoveSlot?.(scheduleIndex)}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="yowan-admin-event-slots__grid" style={slotGridStyle}>
                <div className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.scheduleKind}</span>
                  <select
                    className="c-form__control"
                    {...register(`schedules.${schedulePosition}.scheduleKind`, {
                      onChange: (event) => updateScheduleAutoLabel(schedulePosition, { scheduleKind: event.target.value }),
                    })}
                  >
                    {(content.form.options.scheduleKinds || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {renderFieldNote(content.form.scheduleKindHelpText)}
                </div>

                <div className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.weekday}</span>
                  <select
                    className="c-form__control"
                    {...register(`schedules.${schedulePosition}.weekday`, {
                      onChange: (event) => updateScheduleAutoLabel(schedulePosition, { weekday: event.target.value }),
                    })}
                  >
                    {(content.form.options.weekday || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.scheduleLabel}</span>
                  <input
                    className="c-form__control"
                    type="text"
                    {...register(`schedules.${schedulePosition}.scheduleLabel`, {
                      onBlur: () => updateScheduleAutoLabel(schedulePosition),
                    })}
                  />
                  {renderFieldNote(content.form.scheduleLabelHelpText)}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {scheduleLabels.startAt}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="time"
                    {...register(`schedules.${schedulePosition}.startTime`, { required: content.form.errors.required })}
                  />
                  {errors?.schedules?.[schedulePosition]?.startTime ? (
                    <em className="yowan-admin-form__error">{errors.schedules[schedulePosition].startTime.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {scheduleLabels.endAt}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="time"
                    {...register(`schedules.${schedulePosition}.endTime`, { required: content.form.errors.required })}
                  />
                  {errors?.schedules?.[schedulePosition]?.endTime ? (
                    <em className="yowan-admin-form__error">{errors.schedules[schedulePosition].endTime.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.facility}</span>
                  <select
                    className="c-form__control"
                    {...register(`schedules.${schedulePosition}.facilityId`, {
                      onChange: () => setValue?.(`schedules.${schedulePosition}.roomId`, '', { shouldDirty: true, shouldValidate: true }),
                    })}
                  >
                    {(content.form.options.facilities || []).map((option) => (
                      <option key={option.value} value={option.value} disabled={option.disabled === true}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {renderFieldNote(content.form.facilityHelpText)}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.room}</span>
                  {(() => {
                    const selectedScheduleFacilityId = watch
                      ? watch(`schedules.${schedulePosition}.facilityId`)
                      : watchedSchedules?.[schedulePosition]?.facilityId;
                    const selectedScheduleRoomId = watch
                      ? watch(`schedules.${schedulePosition}.roomId`)
                      : watchedSchedules?.[schedulePosition]?.roomId;

                    return (
                      <select
                        className="c-form__control"
                        disabled={!selectedScheduleFacilityId}
                        {...register(`schedules.${schedulePosition}.roomId`)}
                      >
                        {getRoomOptionsForFacility(
                          content.form.options.rooms || [],
                          selectedScheduleFacilityId,
                          selectedScheduleRoomId,
                        ).map((option) => (
                          <option key={option.value} value={option.value} disabled={option.disabled === true}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                  {renderFieldNote(content.form.roomHelpText)}
                </label>

                <div className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.locationDisplayMode}</span>
                  <div className="c-form__choices">
                    {(content.form.options.locationDisplayModes || []).map((option) => (
                      <label key={option.value} className="c-form__choice">
                        <input
                          type="radio"
                          value={option.value}
                          {...register(`schedules.${schedulePosition}.locationIsPublic`)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {renderFieldNote(content.form.locationDisplayModeHelpText)}
                </div>

                {!isPublicLocationChoice(watchedSchedules?.[schedulePosition]?.locationIsPublic ?? 'true') ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{scheduleLabels.publicLocationLabel}</span>
                    <input className="c-form__control" type="text" {...register(`schedules.${schedulePosition}.publicLocationLabel`)} />
                    {renderFieldNote(content.form.publicLocationLabelHelpText)}
                  </label>
                ) : null}

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.publicLocationNote}</span>
                  <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`schedules.${schedulePosition}.publicLocationNote`)} />
                  {renderFieldNote(content.form.publicLocationNoteHelpText)}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.privateLocationNote}</span>
                  <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`schedules.${schedulePosition}.privateLocationNote`)} />
                  {renderFieldNote(content.form.privateLocationNoteHelpText)}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.capacity}</span>
                  <input className="c-form__control" type="number" min="0" step="1" {...register(`schedules.${schedulePosition}.capacity`)} />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">{scheduleLabels.fee}</span>
                  <input className="c-form__control" type="number" min="0" step="100" {...register(`schedules.${schedulePosition}.fee`)} />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {scheduleLabels.instructor}
                    <em className="c-form__required">*</em>
                  </span>
                  <select
                    className="c-form__control"
                    {...register(`schedules.${schedulePosition}.instructorId`, { required: content.form.errors.required })}
                  >
                    {(content.form.options.instructors || []).map((option) => (
                      <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors?.schedules?.[schedulePosition]?.instructorId ? (
                    <em className="yowan-admin-form__error">{errors.schedules[schedulePosition].instructorId.message}</em>
                  ) : null}
                </label>

                <AdminBooleanToggleField
                  label={scheduleLabels.isActive}
                  name={`schedules.${schedulePosition}.isActive`}
                  register={register}
                />

                <input type="hidden" defaultValue="" {...register(`schedules.${schedulePosition}.remarks`)} />
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {slotLabels ? (
        <section className="yowan-admin-event-slots" style={slotSectionStyle}>
          <div style={slotHeaderStyle}>
            <div>
              <h2 style={slotTitleStyle}>{slotLabels.sectionTitle}</h2>
              <p style={slotLeadStyle}>{slotLabels.sectionLead}</p>
            </div>
            <button type="button" style={slotButtonStyle} onClick={onAddSlot}>
              + {slotLabels.addButton}
            </button>
          </div>

          {content.form.slotHelpText ? (
            <p className="yowan-admin-form__field-note" style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.45 }}>
              {content.form.slotHelpText}
            </p>
          ) : null}

          {slotIndexes.map((slotIndex, slotPosition) => (
            <div key={slotIndex} className="yowan-admin-event-slots__card" style={slotCardStyle}>
              <input type="hidden" {...register(`slots.${slotPosition}.id`)} />
              <div style={slotCardHeaderStyle}>
                <h3 style={slotTitleStyle}>{slotLabels.itemTitle} {slotPosition + 1}</h3>
                {slotIndexes.length > 1 ? (
                  <button
                    type="button"
                    aria-label={`${slotLabels.itemTitle} ${slotPosition + 1} を削除`}
                    style={slotRemoveButtonStyle}
                    onClick={() => onRemoveSlot?.(slotIndex)}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="yowan-admin-event-slots__grid" style={slotGridStyle}>
                {slotLabels.title ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.title}</span>
                    <input
                      className="c-form__control"
                      type="text"
                      {...register(`slots.${slotPosition}.title`)}
                    />
                    {renderFieldNote(content.form.slotTitleHelpText)}
                  </label>
                ) : null}

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.startAt}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="datetime-local"
                    {...register(`slots.${slotPosition}.startAt`, { required: content.form.errors.required })}
                  />
                  {errors?.slots?.[slotPosition]?.startAt ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].startAt.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.endAt}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="datetime-local"
                    {...register(`slots.${slotPosition}.endAt`, { required: content.form.errors.required })}
                  />
                  {errors?.slots?.[slotPosition]?.endAt ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].endAt.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.facility}
                  </span>
                  <select
                    className="c-form__control"
                    {...register(`slots.${slotPosition}.facilityId`, {
                      onChange: () => setValue?.(`slots.${slotPosition}.roomId`, '', { shouldDirty: true, shouldValidate: true }),
                    })}
                  >
                    {(content.form.options.facilities || []).map((option) => (
                      <option key={option.value} value={option.value} disabled={option.disabled === true}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {renderFieldNote(content.form.facilityHelpText)}
                  {errors?.slots?.[slotPosition]?.facilityId ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].facilityId.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.room}
                  </span>
                  {(() => {
                    const selectedSlotFacilityId = watch
                      ? watch(`slots.${slotPosition}.facilityId`)
                      : watchedSlots?.[slotPosition]?.facilityId;
                    const selectedSlotRoomId = watch
                      ? watch(`slots.${slotPosition}.roomId`)
                      : watchedSlots?.[slotPosition]?.roomId;

                    return (
                      <select
                        className="c-form__control"
                        disabled={!selectedSlotFacilityId}
                        {...register(`slots.${slotPosition}.roomId`)}
                      >
                        {getRoomOptionsForFacility(
                          content.form.options.rooms || [],
                          selectedSlotFacilityId,
                          selectedSlotRoomId,
                        ).map((option) => (
                          <option key={option.value} value={option.value} disabled={option.disabled === true}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                  {renderFieldNote(content.form.roomHelpText)}
                  {errors?.slots?.[slotPosition]?.roomId ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].roomId.message}</em>
                  ) : null}
                </label>

                {slotLabels.locationDisplayMode ? (
                  <div className="c-form__field">
                    <span className="c-form__label">{slotLabels.locationDisplayMode}</span>
                    <select
                      className="c-form__control"
                      {...register(`slots.${slotPosition}.locationDisplayMode`)}
                    >
                      {(content.form.options.locationDisplayModes || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {renderFieldNote(content.form.locationDisplayModeHelpText)}
                  </div>
                ) : null}

                {slotLabels.publicLocationLabel ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.publicLocationLabel}</span>
                    <input
                      className="c-form__control"
                      type="text"
                      {...register(`slots.${slotPosition}.publicLocationLabel`)}
                    />
                    {renderFieldNote(content.form.publicLocationLabelHelpText)}
                  </label>
                ) : null}

                {slotLabels.publicLocationNote ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.publicLocationNote}</span>
                    <textarea
                      className="c-form__control c-form__control--textarea"
                      rows="3"
                      {...register(`slots.${slotPosition}.publicLocationNote`)}
                    />
                    {renderFieldNote(content.form.publicLocationNoteHelpText)}
                  </label>
                ) : null}

                {slotLabels.privateLocationNote ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.privateLocationNote}</span>
                    <textarea
                      className="c-form__control c-form__control--textarea"
                      rows="3"
                      {...register(`slots.${slotPosition}.privateLocationNote`)}
                    />
                    {renderFieldNote(content.form.privateLocationNoteHelpText)}
                  </label>
                ) : null}

                {slotLabels.thingsToBring ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.thingsToBring}</span>
                    <textarea
                      className="c-form__control c-form__control--textarea"
                      rows="3"
                      {...register(`slots.${slotPosition}.thingsToBring`)}
                    />
                    {renderFieldNote(content.form.thingsToBringHelpText)}
                  </label>
                ) : null}

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.capacity}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="number"
                    min="0"
                    step="1"
                    {...register(`slots.${slotPosition}.capacity`, { required: content.form.errors.required })}
                  />
                  {errors?.slots?.[slotPosition]?.capacity ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].capacity.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.slotFee}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="number"
                    min="0"
                    step="100"
                    {...register(`slots.${slotPosition}.slotFee`, { required: content.form.errors.required })}
                  />
                  {errors?.slots?.[slotPosition]?.slotFee ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].slotFee.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {slotLabels.instructor}
                    <em className="c-form__required">*</em>
                  </span>
                  <select
                    className="c-form__control"
                    {...register(`slots.${slotPosition}.instructorId`, { required: content.form.errors.required })}
                  >
                    {(content.form.options.instructors || []).map((option) => (
                      <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors?.slots?.[slotPosition]?.instructorId ? (
                    <em className="yowan-admin-form__error">{errors.slots[slotPosition].instructorId.message}</em>
                  ) : null}
                </label>

                {slotLabels.showInstructorProfile ? (
                  <AdminBooleanToggleField
                    label={slotLabels.showInstructorProfile}
                    name={`slots.${slotPosition}.showInstructorProfile`}
                    register={register}
                    helpText={content.form.slotShowInstructorProfileHelpText}
                  />
                ) : null}

                <AdminBooleanToggleField
                  label={slotLabels.isOpen}
                  name={`slots.${slotPosition}.isOpen`}
                  register={register}
                />

                <AdminBooleanToggleField
                  label={slotLabels.isActive}
                  name={`slots.${slotPosition}.isActive`}
                  register={register}
                />

                {slotLabels.remarks ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{slotLabels.remarks}</span>
                    <textarea className="c-form__control c-form__control--textarea" rows="3" {...register(`slots.${slotPosition}.remarks`)} />
                  </label>
                ) : null}
              </div>
            </div>
          ))}
        </section>
      ) : null}
    </>
  );
};

export const AdminAccessEditorFields = ({
  content,
  register,
  errors,
  memberLocked = false,
  lockedMemberId = '',
}) => {
  const memberSelect = memberLocked ? (
    <>
      <input type="hidden" {...register('memberId', { required: content.form.errors.required })} />
      <select className="c-form__control" disabled defaultValue={lockedMemberId}>
        {content.form.memberOptions.map((option) => (
          <option key={option.value || 'empty'} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </>
  ) : (
    <select
      className="c-form__control"
      {...register('memberId', { required: content.form.errors.required })}
    >
      {content.form.memberOptions.map((option) => (
        <option key={option.value || 'empty'} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
  const requireAtLeastOne = (value) => (
    Array.isArray(value) && value.length > 0
      ? true
      : content.form.errors.selectAtLeastOne || content.form.errors.required
  );

  return (
    <>
      <div className="c-form__field">
        <span className="c-form__label">
          {content.form.labels.memberId}
          <em className="c-form__required">*</em>
        </span>
        {memberSelect}
        {errors?.memberId ? (
          <em className="yowan-admin-form__error">{errors.memberId.message}</em>
        ) : null}
        {content.form.memberIdHelpText ? (
          <p
            className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
          >
            {content.form.memberIdHelpText}
          </p>
        ) : null}
      </div>

      {(content.form.accessBindings || []).map((binding) => {
        if (binding.type === 'checkbox-group') {
          return (
            <div key={binding.name} className="c-form__field">
              <span className="c-form__label">{binding.label}</span>
              <div className="yowan-admin-form__choice-group">
                {binding.options.map((option) => (
                  <label key={option.value} className="c-form__choice yowan-admin-form__toggle-choice">
                    <input
                      type="checkbox"
                      value={option.value}
                      {...register(binding.name, { validate: requireAtLeastOne })}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors?.[binding.name] ? (
                <em
                  className="yowan-admin-form__error"
                >
                  {errors[binding.name].message}
                </em>
              ) : null}
              {binding.helpText ? (
                <p
                  className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
                >
                  {binding.helpText}
                </p>
              ) : null}
            </div>
          );
        }

        if (binding.type === 'select') {
          return (
            <div key={binding.name} className="c-form__field">
              <span className="c-form__label">{binding.label}</span>
              <select className="c-form__control" {...register(binding.name)}>
                {binding.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {binding.helpText ? (
                <p
                  className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
                >
                  {binding.helpText}
                </p>
              ) : null}
            </div>
          );
        }

        return null;
      })}

      <AdminBooleanToggleField
        label={content.form.labels.isActive}
        name="isActive"
        register={register}
        helpText={content.form.isActiveHelpText}
      />
    </>
  );
};

export const AdminListToolbar = ({
  searchValue,
  onSearchChange,
  sortValue,
  onSortChange,
  metaText,
  searchPlaceholder = 'タイトル / slug / 公開',
  children = null,
  sortOptions = [
    { value: 'published-desc', label: '日付が新しい順' },
    { value: 'published-status', label: '公開ステータス順' },
    { value: 'slug-asc', label: 'slug 昇順' },
    { value: 'title-asc', label: 'タイトル昇順' },
    { value: 'title-desc', label: 'タイトル降順' },
  ],
}) => (
  <div className="yowan-admin-toolbar c-surface-content">
    <div className="yowan-admin-toolbar__group">
      <label className="c-form__field yowan-admin-toolbar__field">
        <span className="c-form__label">検索</span>
        <input
          className="c-form__control yowan-admin-toolbar__search"
          type="search"
          value={searchValue}
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
        />
      </label>
    </div>

    <div className="yowan-admin-toolbar__group">
      <label className="c-form__field yowan-admin-toolbar__field">
        <span className="c-form__label">並び替え</span>
        <select
          className="c-form__control yowan-admin-toolbar__sort"
          value={sortValue}
          onChange={onSortChange}
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>

    {children}

    <p className="yowan-admin-toolbar__meta">{metaText}</p>
  </div>
);

export const AdminListPagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="yowan-blog-pagination">
      <button
        type="button"
        className="yowan-blog-pagination__link"
        disabled={currentPage === 1}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
      >
        Prev
      </button>
      <div className="yowan-blog-pagination__numbers">
        {Array.from({ length: totalPages }, (_, index) => {
          const pageNumber = index + 1;

          return (
            <button
              key={pageNumber}
              type="button"
              className={`yowan-blog-pagination__link${pageNumber === currentPage ? ' is-active' : ''}`}
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="yowan-blog-pagination__link"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
      >
        Next
      </button>
    </div>
  );
};

const useAdminPageMetadata = (routeKey) => {
  const metadata = getYowanRouteMeta(routeKey);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  return metadata;
};

const useAdminShellState = () => {
  const shellRef = useRef(null);
  const resizingRef = useRef(false);
  const [compactSidebarMode, setCompactSidebarMode] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(232);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncSidebarState = (event) => {
      setCompactSidebarMode(event.matches);
      setSidebarHidden(event.matches);
    };

    syncSidebarState(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncSidebarState);
      return () => mediaQuery.removeEventListener('change', syncSidebarState);
    }

    mediaQuery.addListener(syncSidebarState);
    return () => mediaQuery.removeListener(syncSidebarState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!resizingRef.current || !shellRef.current) {
        return;
      }

      const bounds = shellRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - bounds.left;

      if (rawWidth <= 132) {
        setSidebarHidden(true);
        return;
      }

      const nextWidth = Math.min(Math.max(rawWidth, 168), 420);
      setSidebarWidth(nextWidth);
      setSidebarHidden(false);
    };

    const handlePointerUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  return {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  };
};

export const AdminBlogShell = ({ activeNavHref, children, routeKey }) => {
  const adminContent = yowanPageContent[ADMIN_ROUTE_KEY];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { currentUser } = useYowanAuth();
  useAdminPageMetadata(routeKey);
  const {
    compactSidebarMode,
    resizingRef,
    shellRef,
    sidebarHidden,
    sidebarWidth,
    setSidebarHidden,
    setSidebarWidth,
  } = useAdminShellState();
  const visibleNavGroups = useMemo(
    () => filterStagingSwitchNavGroups(adminContent.navGroups, canUseStagingSwitch(currentUser)),
    [adminContent.navGroups, currentUser]
  );

  return (
    <Section className="yowan-admin-page">
      <PageContainer>
        <div
          ref={shellRef}
          className={`yowan-admin-shell${sidebarHidden ? ' yowan-admin-shell--sidebar-hidden' : ''}`}
          style={{ '--admin-sidebar-width': `${sidebarWidth}px` }}
        >
          <aside className="yowan-admin-shell__sidebar">
            <div className="yowan-admin-sidebar">
              {!sidebarHidden ? (
                <button
                  type="button"
                  className="yowan-admin-shell__toggle yowan-admin-shell__toggle--close"
                  aria-label="左ナビを隠す"
                  aria-expanded="true"
                  onClick={() => setSidebarHidden(true)}
                >
                  <span aria-hidden="true">‹</span>
                </button>
              ) : null}
              <Heading as="h1" align="left">
                <Link
                  className="yowan-admin-sidebar__home-link"
                  to={appendYowanContentPreviewQuery('/admin', preservedDomainSearch)}
                >
                  {adminContent.header.title}
                </Link>
              </Heading>
              {visibleNavGroups.map((group) => (
                <section key={group.title} className="yowan-admin-sidebar__group">
                  <h2 className="yowan-admin-sidebar__group-title">{group.title}</h2>
                  <ul className="yowan-admin-sidebar__list">
                    {group.items.map((item) => (
                      <li key={item.label}>
                        <Link
                          to={appendYowanContentPreviewQuery(item.href, preservedDomainSearch)}
                          aria-current={item.href === activeNavHref ? 'page' : undefined}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </aside>

          {sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__toggle yowan-admin-shell__toggle--open"
              aria-label="左ナビを表示"
              aria-expanded="false"
              onClick={() => {
                setSidebarWidth(Math.max(sidebarWidth, 208));
                setSidebarHidden(false);
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          ) : null}

          {!sidebarHidden ? (
            <button
              type="button"
              className="yowan-admin-shell__resizer"
              aria-label="左ナビの幅を調整"
              onPointerDown={(event) => {
                event.preventDefault();
                resizingRef.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
            />
          ) : null}

          <div className="yowan-admin-shell__main">
            <div
              className="yowan-admin-shell__content yowan-admin-stack"
              onClick={() => {
                if (compactSidebarMode && !sidebarHidden) {
                  setSidebarHidden(true);
                }
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </PageContainer>
    </Section>
  );
};

export const AdminContentListPage = ({ contentKey, routeKey, activeNavHref }) => {
  const content = yowanPageContent[contentKey];
  const preservedDomainSearch = usePreservedDomainSearch();
  const listApi = adminContentListApis[contentKey] || null;
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    if (!listApi) {
      const fixtureRows = content.rows || [];
      setRows(fixtureRows);
      setApiCount(fixtureRows.length);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    listApi({ page: currentPage, pageSize: ADMIN_CONTENT_PAGE_SIZE })
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
          setApiCount(payload.count);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setApiCount(0);
          setLoadError('一覧を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, listApi]);

  if (!content) {
    return null;
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [row.title, row.slug, row.authorName, getAdminContentPublicationStatus(row, contentKey).label]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : rows;
  const sortedRows = sortAdminContentRows(filteredRows, sortKey);
  const totalPages = listApi && !normalizedQuery
    ? Math.max(1, Math.ceil(apiCount / ADMIN_CONTENT_PAGE_SIZE))
    : Math.max(1, Math.ceil(sortedRows.length / ADMIN_CONTENT_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = listApi && !normalizedQuery
    ? (safeCurrentPage - 1) * ADMIN_CONTENT_PAGE_SIZE
    : (safeCurrentPage - 1) * ADMIN_CONTENT_PAGE_SIZE;
  const visibleRows = listApi && !normalizedQuery
    ? sortedRows
    : sortedRows.slice(startIndex, startIndex + ADMIN_CONTENT_PAGE_SIZE);
  const metaRowCount = normalizedQuery ? sortedRows.length : apiCount;
  const publishedAtColumnLabel = adminContentPublishedAtColumnLabels[contentKey] || '公開日時';

  return (
    <AdminBlogShell activeNavHref={activeNavHref} routeKey={routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanContentPreviewQuery(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${metaRowCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={adminContentTitleColumnStyle}>タイトル / Slug</th>
              <th scope="col">公開</th>
              <th scope="col" style={adminContentDateColumnStyle}>{publishedAtColumnLabel}</th>
              <th scope="col" style={adminContentDateColumnStyle}>作成日</th>
              <th scope="col" style={adminContentDateColumnStyle}>更新日</th>
              <th scope="col" style={adminContentActionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = getAdminContentPublicationStatus(row, contentKey);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td style={adminContentTitleColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.title}</p>
                    <p className="yowan-admin-resource-table__subline">{row.slug}</p>
                    {contentKey === '/admin/content/blog' && row.authorName ? (
                      <p className="yowan-admin-resource-table__subline">著者: {row.authorName}</p>
                    ) : null}
                  </td>
                  <td>
                    <span
                      className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}
                    >
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={adminContentDateColumnStyle}>
                    {formatAdminContentPublishedAt(row, contentKey)}
                  </td>
                  <td style={adminContentDateColumnStyle}>{formatYowanAdminDateTime(row.createdAt)}</td>
                  <td style={adminContentDateColumnStyle}>{formatYowanAdminDateTime(row.updatedAt)}</td>
                  <td style={adminContentActionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions" style={adminContentActionGroupStyle}>
                      <Link to={appendYowanContentPreviewQuery(row.editHref, preservedDomainSearch)}>編集</Link>
                      {row.publicHref ? (
                        <Link to={appendYowanContentPreviewQuery(row.publicHref, preservedDomainSearch)}>公開面</Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!visibleRows.length ? (
              <tr>
                <td colSpan="7">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '表示できるレコードがありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

const AdminBlogListPage = () => (
  <AdminContentListPage
    contentKey="/admin/content/blog"
    routeKey="/admin/content/blog"
    activeNavHref="/admin/content/blog"
  />
);

export default AdminBlogListPage;
